import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, Mail, Calendar, Edit, LogOut } from 'lucide-react';
import userAPI from '../../api/user';
import toast from '../../utils/toast';

const ProfileSettings = () => {
  const { user, updateUser, logout } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);


  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      if (response.data) {
        updateUser(response.data); // Use context update method
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      fetchUserProfile();
    }
  }, [user]);
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
        <Link
          to="/profile"
          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Profile
        </Link>

      </div>

      <div className="space-y-6">
        {/* Profile Picture */}
        <div className="flex items-center space-x-4">
          <div className="h-20 w-20 rounded-full bg-gray-100 border-4 border-white flex items-center justify-center">
            {user?.profile_picture ? (
              <img
                src={user.profile_picture}
                alt={`${user.first_name} ${user.last_name}`}
                className="h-16 w-16 rounded-full"
              />
            ) : (
              <User className="h-10 w-10 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {user?.first_name} {user?.last_name}
            </h3>
            <p className="text-sm text-gray-500">Your profile picture</p>
          </div>
        </div>

        {/* Personal Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <div className="flex items-center text-sm text-gray-500">
                <User className="h-4 w-4 mr-2" />
                Full Name
              </div>
              <div className="text-gray-900">
                {user?.first_name} {user?.middle_name && `${user.middle_name} `}{user?.last_name}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center text-sm text-gray-500">
                <Mail className="h-4 w-4 mr-2" />
                Email Address
              </div>
              <div className="text-gray-900">{user?.email}</div>
            </div>

            {user?.phone && (
              <div className="space-y-1">
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Phone Number
                </div>
                <div className="text-gray-900 flex items-center">
                  {user.phone}
                  {user.phone_verified && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            )}

            {user?.date_of_birth && (
              <div className="space-y-1">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date of Birth
                </div>
                <div className="text-gray-900">
                  {new Date(user.date_of_birth).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ID Verification Status */}
        {user?.id_verification_status && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">ID Verification</h3>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${user.id_verification_status === 'verified'
                ? 'bg-green-100 text-green-800'
                : user.id_verification_status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
              {user.id_verification_status.charAt(0).toUpperCase() + user.id_verification_status.slice(1)}
            </div>
            {user.id_uploaded_at && (
              <p className="text-sm text-gray-500 mt-2">
                Uploaded on {new Date(user.id_uploaded_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>
      {/* After all profile sections */}
      <div className="border-t border-red-200 pt-6 mt-8">
        <h3 className="text-lg font-medium text-red-900 mb-4">Account</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-sm font-medium text-red-900">Sign out of your account</h4>
              <p className="text-sm text-red-700 mt-1">
                You'll need to sign in again to access your account.
              </p>
            </div>
            <button
              onClick={() => logout()}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;